"use client";

import { useState, useRef, useEffect } from "react";
import { useDebounce } from "@/shared/hooks/useDebounce";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Field, FieldLabel } from "@/components/ui/field";
import { Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { authApi, type UserSearchResult } from "@/features/auth/services/authApi";

interface UserSearchSelectProps {
  onSelect: (user: UserSearchResult | null) => void;
  selectedUserId?: string;
  selectedUserName?: string;
  disabled?: boolean;
  label?: string;
  required?: boolean;
}

export const UserSearchSelect = ({
  onSelect,
  selectedUserId,
  selectedUserName,
  disabled = false,
  label = "User",
  required = false,
}: UserSearchSelectProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState<UserSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const debouncedSearch = useDebounce(search, 300);
  const inputRef = useRef<HTMLInputElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Fetch users when search term changes
  useEffect(() => {
    if (!debouncedSearch) return;

    const fetchUsers = async () => {
      setIsLoading(true);
      try {
        const data = await authApi.searchUsers(debouncedSearch, 1, 10);
        setUsers(data.users);
      } catch {
        setUsers([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, [debouncedSearch]);

  const handleSelect = (user: UserSearchResult) => {
    onSelect(user);
    setSearch("");
    setOpen(false);
  };

  return (
    <Field>
      <FieldLabel>
        {label} {required && <span className="text-red-500">*</span>}
      </FieldLabel>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <Input
            ref={inputRef}
            placeholder={`Search ${label.toLowerCase()}...`}
            value={search || selectedUserName || ""}
            onChange={(e) => {
              setSearch(e.target.value);
              if (!open) setOpen(true);
              if (selectedUserId) {
                onSelect(null);
              }
            }}
            onFocus={() => setOpen(true)}
            disabled={disabled}
          />
        </PopoverAnchor>
        <PopoverContent
          ref={popoverRef}
          className="p-0 w-[--radix-popover-trigger-width] max-w-[calc(100vw-2rem)]"
          align="start"
          sideOffset={4}
          avoidCollisions
          collisionPadding={16}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <div className="max-h-[300px] overflow-y-auto">
            {isLoading ? (
              <div className="p-4 text-center">
                <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                <p className="text-sm text-muted-foreground mt-2">
                  Searching...
                </p>
              </div>
            ) : search && users.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                No users found.
              </div>
            ) : (
              (debouncedSearch ? users : []).map((user) => (
                <div
                  key={user.id}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-accent hover:text-accent-foreground text-sm",
                    selectedUserId === user.id && "bg-accent"
                  )}
                  onClick={() => handleSelect(user)}
                >
                  {selectedUserId === user.id && (
                    <Check className="h-4 w-4 flex-shrink-0" />
                  )}
                  <span className="flex-1">
                    {user.firstName} {user.otherNames}
                  </span>
                </div>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>
    </Field>
  );
};
